import type { ProductDto } from '@repo/api/web/models'
import { injectable } from 'inversify'

import type { CategoryDto } from '#Products/models/Pim/CategoryDto'

/** Рекомендуемый предел длины meta description (сниппет поисковика) */
const DESCRIPTION_MAX_LENGTH = 160

/** Рекомендуемый размер og:image (Facebook/VK/Telegram) */
export const OG_IMAGE_WIDTH = 1200
export const OG_IMAGE_HEIGHT = 630

/**
 * SEO-мета товара/категории: приоритет у меты из админки
 * (MetaTitle/MetaDescription/OgImage, MR 9b), при её отсутствии — авто-генерация
 * по шаблонам SKUBY-SEO-MODULE-SPEC.md §5. Это и есть «гибрид» из спеки:
 * осмысленная мета есть всегда, ручная её оверрайдит.
 */
@injectable()
export class SeoService {
  protected siteName: ReturnType<typeof useSiteName>
  protected img: ReturnType<typeof useImage>
  /**
   * Ref захватывается в конструкторе (setup-контекст): методы сервиса зовут из
   * геттеров useSeoMeta/useHead, а те выполняются при рендере head вне setup —
   * вызов nuxt-композабла оттуда падает «composable outside setup» (см. !37).
   * Сами настройки грузит плагин seoConfigLoader.
   */
  protected seoConfig: ReturnType<typeof useSeoConfig>

  constructor() {
    this.siteName = useSiteName()
    this.img = useImage()
    this.seoConfig = useSeoConfig()
  }

  /**
   * Абсолютный URL og-картинки по id ресурса (ресайз на бэкенде до 1200×630).
   * Абсолютность обязательна — шарящие ссылку мессенджеры/соцсети резолвят
   * og:image без базового URL.
   */
  ogImageById(imageId?: string | null): string | undefined {
    if (!imageId) return undefined

    return this.img(
      imageId,
      { width: OG_IMAGE_WIDTH, height: OG_IMAGE_HEIGHT },
      { provider: 'skubyApi' },
    )
  }

  /**
   * og-картинка товара: заданная в админке, иначе первое изображение товара,
   * иначе дефолтная картинка витрины.
   */
  productOgImage(product: ProductDto): string | undefined {
    return (
      this.ogImageById(product.ogImageId ?? product.images?.[0]?.id)
      ?? this.defaultOgImage()
    )
  }

  /** og-картинка категории: заданная в админке, иначе дефолтная витрины */
  categoryOgImage(category?: CategoryDto): string | undefined {
    return this.ogImageById(category?.ogImageId) ?? this.defaultOgImage()
  }

  /** Дефолтная og-картинка витрины из per-channel настроек (может отсутствовать) */
  defaultOgImage(): string | undefined {
    return this.ogImageById(this.seoConfig.value?.defaultOgImageId)
  }

  /**
   * Title карточки товара: мета из админки, иначе имя товара
   * (суффикс « — {Витрина}» добавит titleTemplate)
   */
  productTitle(product: ProductDto): string {
    return product.metaTitle || (product.wellKnownAttributes?.Name ?? '')
  }

  /**
   * Description карточки товара: мета из админки, иначе Description-атрибут
   * с обрезкой до ~160, иначе шаблон из имени/цены
   */
  productDescription(product: ProductDto): string {
    if (product.metaDescription) return product.metaDescription

    const description = product.wellKnownAttributes?.Description
    if (description) return this.truncate(this.collapseWhitespace(description))

    const name = product.wellKnownAttributes?.Name ?? ''
    const price = product.wellKnownAttributes?.Price

    const pricePart = price
      ? ` по цене ${price.toLocaleString('ru-RU')} ₽`
      : ''

    return this.truncate(
      `Купить ${name}${pricePart} в интернет-магазине ${this.siteName.value}. Доставка, оплата онлайн.`,
    )
  }

  /**
   * Title страницы категории: мета из админки, иначе имя категории (для
   * главной без категории — undefined, сработает дефолтный title витрины)
   */
  categoryTitle(category?: CategoryDto): string | undefined {
    return category?.metaTitle || category?.name || undefined
  }

  /**
   * Description страницы категории/главной: мета из админки, иначе шаблон
   */
  categoryDescription(category?: CategoryDto): string {
    if (category?.metaDescription) return category.metaDescription

    return category?.name
      ? this.truncate(
          `${category.name} — каталог интернет-магазина ${this.siteName.value}. Доставка, оплата онлайн.`,
        )
      : this.truncate(
          `Интернет-магазин ${this.siteName.value}: каталог товаров, доставка, оплата онлайн.`,
        )
  }

  protected collapseWhitespace(value: string): string {
    return value.replace(/\s+/g, ' ').trim()
  }

  protected truncate(value: string, maxLength: number = DESCRIPTION_MAX_LENGTH): string {
    if (value.length <= maxLength) return value

    const cut = value.slice(0, maxLength)
    const lastSpace = cut.lastIndexOf(' ')

    return `${cut.slice(0, lastSpace > 0 ? lastSpace : maxLength)}…`
  }
}
